import React from 'react';
import { TranslationAdminDto, CreateTranslationDto, UpdateTranslationDto, LanguageAdminDto } from '../../types';

interface TranslationModalProps {
    isOpen: boolean;
    onClose: () => void;
    isCreating: boolean;
    selectedTranslation: TranslationAdminDto | null;
    createFormData: CreateTranslationDto;
    setCreateFormData: React.Dispatch<React.SetStateAction<CreateTranslationDto>>;
    updateFormData: UpdateTranslationDto;
    setUpdateFormData: React.Dispatch<React.SetStateAction<UpdateTranslationDto>>;
    handleSaveTranslation: () => Promise<void>;
    languages: LanguageAdminDto[];           // ← Pass languages from parent
}

const TranslationModal: React.FC<TranslationModalProps> = ({
    isOpen,
    onClose,
    isCreating,
    selectedTranslation: _selectedTranslation,
    createFormData,
    setCreateFormData,
    updateFormData,
    setUpdateFormData,
    handleSaveTranslation,
    languages,
}) => {
    if (!isOpen) return null;

    const activeLanguages = languages.filter(l => l.isActive);

    if (activeLanguages.length === 0) {
        return (
            <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
                    <h2 className="text-xl font-bold text-red-600 mb-4">No Active Languages</h2>
                    <p className="text-gray-700 mb-6">
                        You cannot create or edit translations because there are no active languages.
                        Please go to the <strong>Setup</strong> tab and add at least one language first.
                    </p>
                    <button
                        onClick={onClose}
                        className="w-full px-4 py-2 bg-gray-800 text-white rounded"
                    >
                        Close
                    </button>
                </div>
            </div>
        );
    }

    const handleValueChange = (langCode: string, value: string) => {
        if (isCreating) {
            setCreateFormData(prev => ({
                ...prev,
                translations: { ...prev.translations, [langCode]: value }
            }));
        } else {
            setUpdateFormData(prev => ({
                ...prev,
                translations: { ...prev.translations, [langCode]: value }
            }));
        }
    };

    const handleSubmit = async () => {
        await handleSaveTranslation();
    };

    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
                <div className="p-6 border-b">
                    <h2 className="text-2xl font-bold">
                        {isCreating ? 'Create Translation Key' : 'Edit Translation'}
                    </h2>
                </div>

                <div className="p-6 space-y-6">
                    {isCreating && (
                        <div>
                            <label className="block text-sm font-medium mb-1">Key</label>
                            <input
                                value={createFormData.key}
                                onChange={e => setCreateFormData({ ...createFormData, key: e.target.value })}
                                className="w-full border p-3 rounded"
                                placeholder="order.shipped.buyer"
                            />
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-medium mb-1">Description (optional)</label>
                        <textarea
                            value={isCreating ? createFormData.description || '' : updateFormData.description || ''}
                            onChange={e => {
                                if (isCreating) {
                                    setCreateFormData({ ...createFormData, description: e.target.value });
                                } else {
                                    setUpdateFormData({ ...updateFormData, description: e.target.value });
                                }
                            }}
                            className="w-full border p-3 rounded"
                            rows={2}
                        />
                    </div>

                    <div>
                        <h3 className="font-semibold mb-3">Translations</h3>
                        <div className="space-y-5">
                            {activeLanguages.map(lang => (
                                <div key={lang.code}>
                                    <label className="block mb-1 font-medium">
                                        {lang.flagEmoji} {lang.nativeName || lang.name} ({lang.code})
                                    </label>
                                    <textarea
                                        rows={3}
                                        value={
                                            isCreating
                                                ? createFormData.translations[lang.code] || ''
                                                : updateFormData.translations[lang.code] || ''
                                        }
                                        onChange={e => handleValueChange(lang.code, e.target.value)}
                                        className="w-full border p-3 rounded"
                                        placeholder={`Translation in ${lang.name}...`}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="p-6 border-t flex justify-end gap-3 bg-gray-50">
                    <button onClick={onClose} className="px-5 py-2 rounded">Cancel</button>
                    <button onClick={handleSubmit} className="px-5 py-2 bg-blue-600 text-white rounded">
                        {isCreating ? 'Create' : 'Save Changes'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TranslationModal;